import React, {
  ComponentType,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { noop } from '@adsk/alloy-react-helpers';

import { hasLocalView } from '../api/attachments';
import { AttachmentDraft } from '../types';

export type ViewsRecords = {
  [draftId: string]: string;
};

type LocalViewsProviderProps = {
  children?: React.ReactNode;
};

export type TLocalViewsContextReturnValue = {
  localViews: ViewsRecords;
  addLocalView: (draft: AttachmentDraft) => void;
  removeLocalViewIfExist: (draftId: string) => void;
};

const LocalViewsContext = React.createContext<TLocalViewsContextReturnValue>({
  localViews: {},
  addLocalView: noop,
  removeLocalViewIfExist: noop,
});

export const useLocalViewsContext = () => React.useContext(LocalViewsContext);

export const useTmpLocalViews = (): TLocalViewsContextReturnValue => {
  const { localViews, addLocalView, removeLocalViewIfExist } =
    useLocalViewsContext();

  return {
    localViews,
    addLocalView,
    removeLocalViewIfExist,
  };
};

export const LocalViewsProvider: ComponentType<LocalViewsProviderProps> = ({
  children,
}) => {
  const [localViews, setLocalViews] = useState<ViewsRecords>({});
  const viewsRef = useRef<ViewsRecords>(localViews);

  const addLocalView = useCallback((draft: AttachmentDraft) => {
    if (!hasLocalView(draft.file.name)) {
      return;
    }

    const viewableLink = URL.createObjectURL(draft.file);

    setLocalViews((prevThumbnails) => ({
      ...prevThumbnails,
      [draft.attachmentId]: viewableLink,
    }));
  }, []);

  const removeLocalViewIfExist = useCallback((draftId: string) => {
    const viewableLink = viewsRef.current[draftId];
    if (viewableLink) {
      URL.revokeObjectURL(viewableLink);

      setLocalViews((prevThumbnails) => {
        const { [draftId]: _, ...rest } = prevThumbnails;
        return rest;
      });
    }
  }, []);

  useEffect(() => {
    viewsRef.current = localViews;
  }, [localViews]);

  useEffect(() => {
    return () => {
      Object.values(viewsRef).forEach((viewableLink) => {
        return URL.revokeObjectURL(viewableLink);
      });
    };
  }, []);

  return (
    <LocalViewsContext.Provider
      value={{
        localViews,
        addLocalView,
        removeLocalViewIfExist,
      }}
    >
      {children}
    </LocalViewsContext.Provider>
  );
};
